"use client";

import { AnimatedCursor, getCursorTarget } from "./animated-cursor";
import { springSnappy, stagger } from "./motion-tokens";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { useEffect, useState, useRef } from "react";
import { TextInitial } from "lucide-react";

/**
 * Mock of the real ChordsIcon from the app
 */
function ChordsIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="currentColor"
      stroke="none"
      className={className}
    >
      <text
        x="9"
        y="12.5"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="17"
        fontWeight="600"
        fontFamily="ui-rounded, system-ui, -apple-system, sans-serif"
      >
        G
      </text>
      <text
        x="19.5"
        y="7"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="11"
        fontWeight="700"
        fontFamily="ui-rounded, system-ui, -apple-system, sans-serif"
      >
        #
      </text>
    </svg>
  );
}

function NumbersIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="currentColor"
      stroke="none"
      className={className}
    >
      <text
        x="12"
        y="7.5"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="10.5"
        fontWeight="700"
        fontFamily="ui-rounded, system-ui, -apple-system, sans-serif"
      >
        1
      </text>
      <text
        x="7"
        y="15.5"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="10.5"
        fontWeight="700"
        fontFamily="ui-rounded, system-ui, -apple-system, sans-serif"
      >
        2
      </text>
      <text
        x="17"
        y="15.5"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="10.5"
        fontWeight="700"
        fontFamily="ui-rounded, system-ui, -apple-system, sans-serif"
      >
        3
      </text>
    </svg>
  );
}

type DisplayState = {
  mode: "nashville" | "chord";
  showLyrics: boolean;
};

const STATES: DisplayState[] = [
  { mode: "nashville", showLyrics: true },
  { mode: "chord", showLyrics: true },
  { mode: "chord", showLyrics: false },
  { mode: "nashville", showLyrics: false },
];

const DEMO_SONG = {
  title: "Amazing Grace",
  key: "G",
  bpm: 72,
  sections: [
    {
      name: "Verse 1",
      progressions: [
        {
          items: [
            { spacer: 0, nashville: "1", chord: "G" },
            { spacer: 15, nashville: "4", chord: "C" },
            { spacer: 8, nashville: "1", chord: "G" },
          ],
          lyrics: "Amazing grace how sweet the sound",
        },
        {
          items: [
            { spacer: 5, nashville: "1", chord: "G" },
            { spacer: 16, nashville: "5", chord: "D" },
          ],
          lyrics: "That saved a wretch like me",
        },
        {
          items: [
            { spacer: 2, nashville: "1", chord: "G" },
            { spacer: 15, nashville: "4", chord: "C" },
            { spacer: 5, nashville: "1", chord: "G" },
          ],
          lyrics: "I once was lost but now am found",
        },
        {
          items: [
            { spacer: 4, nashville: "1", chord: "G" },
            { spacer: 8, nashville: "5", chord: "D" },
            { spacer: 4, nashville: "1", chord: "G" },
          ],
          lyrics: "Was blind but now I see",
        },
      ],
    },
  ],
};

const CYCLE_DURATION = 3000;

export function TransposeDemo() {
  const [stateIndex, setStateIndex] = useState(0);
  const [cursorPos, setCursorPos] = useState({ x: 200, y: 14 });
  const [isClicking, setIsClicking] = useState(false);
  const [clickTarget, setClickTarget] = useState<"chords" | "lyrics" | null>(
    null,
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { amount: 0.3 });

  const currentState = STATES[stateIndex] ?? STATES[0];

  useEffect(() => {
    if (!isInView) return;
    let mounted = true;

    const runSequence = async () => {
      await sleep(1500);

      while (mounted) {
        const nextIndex = (stateIndex + 1) % STATES.length;
        const nextState = STATES[nextIndex] ?? STATES[0];
        const currentState = STATES[stateIndex] ?? STATES[0];

        const isTargetingChords = nextState.mode !== currentState.mode;
        const targetId = isTargetingChords
          ? "transpose-chords-toggle"
          : "transpose-lyrics-toggle";

        const pos = getCursorTarget(targetId, "transpose-demo-container");
        if (!pos) {
          await sleep(500);
          continue;
        }

        setCursorPos(pos);
        await sleep(600);
        if (!mounted) return;

        setClickTarget(isTargetingChords ? "chords" : "lyrics");
        setIsClicking(true);
        await sleep(120);
        if (!mounted) return;

        setStateIndex(nextIndex);
        setIsClicking(false);
        setClickTarget(null);
        await sleep(CYCLE_DURATION - 720);
        if (!mounted) return;
      }
    };

    void runSequence();
    return () => {
      mounted = false;
    };
  }, [isInView, stateIndex]);

  return (
    <div
      ref={containerRef}
      id="transpose-demo-container"
      className="relative w-full max-w-lg mx-auto"
    >
      <div className="rounded-xl border border-border-primary bg-surface-foreground overflow-hidden shadow-2xl min-h-[340px]">
        {/* Mock page header */}
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-border-primary bg-surface-primary">
          <span className="text-sm font-bold text-glyphs-primary">
            Sunday Worship
          </span>
          <div className="flex items-center gap-2">
            <motion.button
              id="transpose-chords-toggle"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border-primary text-xs font-medium text-glyphs-primary bg-surface-secondary"
              animate={{
                scale: isClicking && clickTarget === "chords" ? 0.94 : 1,
              }}
              transition={springSnappy}
            >
              {currentState.mode === "chord" ? (
                <ChordsIcon className="size-4" />
              ) : (
                <NumbersIcon className="size-4" />
              )}
              <span className="hidden sm:inline">
                {currentState.mode === "chord" ? "Chords" : "Numbers"}
              </span>
            </motion.button>
            <motion.button
              id="transpose-lyrics-toggle"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border-primary text-xs font-medium text-glyphs-primary bg-surface-secondary transition-opacity"
              style={{ opacity: currentState.showLyrics ? 1 : 0.5 }}
              animate={{
                scale: isClicking && clickTarget === "lyrics" ? 0.94 : 1,
              }}
              transition={springSnappy}
            >
              <TextInitial className="size-4" />
              <span className="hidden sm:inline">Lyrics</span>
            </motion.button>
          </div>
        </div>

        {/* Song title row */}
        <div className="bg-theme-green flex items-center px-4 py-2">
          <span className="text-white font-semibold text-sm flex-1">
            1. {DEMO_SONG.title}
          </span>
          <span className="text-white/70 text-xs font-medium mr-2">
            {DEMO_SONG.bpm}bpm
          </span>
          <span className="text-white font-semibold text-xs">
            Key: {DEMO_SONG.key}
          </span>
        </div>

        {/* Section */}
        {DEMO_SONG.sections.map((section) => (
          <div key={section.name}>
            {/* Section header */}
            <div className="bg-surface-primary shadow-sm px-4 py-1.5 border-b border-border-primary">
              <span className="text-glyphs-primary font-medium text-sm">
                {section.name}
              </span>
            </div>

            {/* Progression rows */}
            <div className="divide-y divide-border-primary/50">
              {section.progressions.map((prog, i) => (
                <div key={i}>
                  {/* Chord progression row */}
                  <div className="bg-surface-foreground px-4 pt-1 pb-0">
                    <div className="inline-flex font-mono text-sm">
                      <AnimatePresence mode="popLayout">
                        {prog.items.map((item, ci) => (
                          <motion.span
                            key={`${currentState.mode}-${i}-${ci}`}
                            className="inline-block text-glyphs-primary font-semibold"
                            style={{
                              marginLeft: `${currentState.showLyrics ? item.spacer : ci === 0 ? 0 : 1}ch`,
                            }}
                            layout
                            initial={{ opacity: 0, y: -8, scale: 0.92 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 8, scale: 0.92 }}
                            transition={{
                              type: "spring",
                              stiffness: 420,
                              damping: 32,
                              delay: ci * stagger.fast,
                            }}
                          >
                            {currentState.mode === "nashville"
                              ? item.nashville
                              : item.chord}
                          </motion.span>
                        ))}
                      </AnimatePresence>
                    </div>
                  </div>

                  {/* Lyric row */}
                  <AnimatePresence>
                    {currentState.showLyrics && (
                      <motion.div
                        className="bg-surface-foreground px-4 pb-1 overflow-hidden"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <p className="text-xs text-glyphs-secondary font-mono">
                          {prog.lyrics}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <AnimatedCursor
        x={cursorPos.x}
        y={cursorPos.y}
        visible={isInView}
        clicking={isClicking}
      />
    </div>
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
